import * as yup from 'yup';

import { FirstNameLength, LastNameLength } from './enums/common';
import { ValidationInfo, ValidationMapping, ValidationTypes } from 'models/validation-schema';
import { Patterns } from 'utils/enums/validation-patterns';

export const fullNameValidationSchema = {
  firstName: yup.string().min(FirstNameLength.Min).max(FirstNameLength.Max).required(),
  lastName: yup.string().min(LastNameLength.Min).max(LastNameLength.Max).required(),
};

export const validateInternalAddressSchema = yup.object().shape({
  name: yup.string().min(1).required(),
  locationId: yup.number().required(),
});

export const validateAddressSchema = yup.object().shape({
  name: yup.string().required(),
  description: yup.string(),
  lat: yup.number().required(),
  lng: yup.number().required(),
});

export const validationSchemaPlace = (required?: boolean): yup.AnyObjectSchema =>
  yup
    .object()
    .shape({
      isLocation: yup.boolean().required(),
    })
    .when('.isLocation', {
      is: true,
      then: (schema) => (required ? schema.concat(validateInternalAddressSchema) : schema.concat(validateInternalAddressSchema).nullable()),
      otherwise: (schema) => (required ? schema.concat(validateAddressSchema) : schema.concat(validateAddressSchema).nullable()),
    });

export const makeYupValidatorFromMapping = <ForPath extends (string | null)[] | null = null>(
  validationMapping: ValidationMapping,
  forPath: ForPath,
): ForPath extends null ? yup.AnyObjectSchema : yup.AnySchema => {
  const path = forPath ? [...(forPath as (string | null)[])] : null; // We duplicate the array to protect the original

  const make = (mapping = validationMapping) => {
    const buildFieldValidation = (validations: ValidationInfo[]): yup.AnySchema => {
      const isRequired = !validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsOptional);

      /** ARRAYS */

      const isArray = validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsArray);

      if (isArray) {
        let yupValidation: yup.ArraySchema<unknown[] | undefined, unknown, '' | undefined> = yup.array();

        const isArrayOf = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.IsArrayOf);

        if (isArrayOf) {
          if (path && path.length > 0) {
            path.shift()!;

            return buildFieldValidation(isArrayOf.items!); // Removed the first part of the path
          }

          yupValidation = yupValidation.of(buildFieldValidation(isArrayOf.items!));
        }

        const arrayMinSize = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.ArrayMinSize);

        if (arrayMinSize) {
          yupValidation = yupValidation.min(arrayMinSize.parameters![0] as number);
        }

        const arrayMaxSize = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.ArrayMaxSize);

        if (arrayMaxSize) {
          yupValidation = yupValidation.min(arrayMaxSize.parameters![0] as number);
        }

        if (isRequired) {
          yupValidation = yupValidation.required();
        }

        return yupValidation;
      }

      /** OBJECTS */

      const isObject = validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsObject);

      if (isObject) {
        let yupValidation: yup.AnyObjectSchema = yup.object();

        const isObjectOf = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.IsObjectOf);

        if (isObjectOf) {
          if (path && path.length > 0) {
            const next = path.shift()!;

            return buildFieldValidation(isObjectOf.nested![next]);
          }

          yupValidation = make(isObjectOf.nested!) as yup.AnyObjectSchema;
        }

        if (isRequired) {
          yupValidation = yupValidation.required();
        }

        return yupValidation;
      }
      /** NUMBERS */

      const isNumber = validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsNumber);

      if (isNumber) {
        const addNumerialValidations = (schema: yup.NumberSchema) => {
          const isMin = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.Min);

          if (isMin) {
            schema = schema.min(isMin.parameters![0] as number);
          }

          const isMax = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.Max);

          if (isMax) {
            schema = schema.max(isMax.parameters![0] as number);
          }

          const isEnum = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.IsEnum);

          if (isEnum) {
            schema = schema.oneOf(isEnum.parameters! as number[]);
          }

          return schema;
        };

        const isLatOrLng = validations.some((validation: ValidationInfo) =>
          [ValidationTypes.IsLatitude, ValidationTypes.IsLongitude].includes(validation.type),
        );

        if (isLatOrLng) {
          let yupValidation = yup.mixed().when({
            is: (value: unknown) => typeof value === 'string' && value.includes(','),
            then: () => yup.string().matches(new RegExp(Patterns.CommaNumber)),
            otherwise: () => addNumerialValidations(yup.number()),
          });

          if (isRequired) {
            yupValidation = yupValidation.required();
          }

          return yupValidation;
        }

        let yupValidation = addNumerialValidations(yup.number());

        if (isRequired) {
          yupValidation = yupValidation.required();
        }

        return yupValidation;
      }

      /** STRINGS */

      const isString = validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsString);

      if (isString) {
        let yupValidation = yup.string();

        const isMinLength = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.MinLength);

        if (isMinLength) {
          yupValidation = yupValidation.min(isMinLength.parameters![0] as number);
        }

        const isMaxLength = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.MaxLength);

        if (isMaxLength) {
          yupValidation = yupValidation.max(isMaxLength.parameters![0] as number);
        }

        const isEnum = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.IsEnum);

        if (isEnum) {
          yupValidation = yupValidation.oneOf(isEnum.parameters! as string[]);
        }

        const isPhoneNumber = validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsPhoneNumber);

        if (isPhoneNumber) {
          yupValidation = yupValidation.matches(new RegExp(Patterns.Phone));
        } else {
          const isEmail = validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsEmail);

          if (isEmail) {
            yupValidation = yupValidation.email();
          }
        }

        const matches = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.Matches);

        if (matches) {
          yupValidation = yupValidation.matches(new RegExp(matches.parameters![0] as string));
        }

        const isUnique = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.UniqueFlag);

        if (isUnique) {
          yupValidation = yupValidation.notOneOf(isUnique.parameters as string[], 'value already exists'); // TODO: replace with translation when implementing validations translations
        }

        if (isRequired) {
          yupValidation = yupValidation.required();
        }

        return yupValidation;
      }

      /** IDS */

      const isId = validations.some((validation: ValidationInfo) => validation.type === ValidationTypes.IsId);

      if (isId) {
        let yupValidation = yup.string();

        const isEnum = validations.find((validation: ValidationInfo) => validation.type === ValidationTypes.IsEnum);

        if (isEnum) {
          yupValidation = yupValidation.oneOf(
            isEnum.parameters!.map((item: unknown) => String(item)),
            () => 'value doesnt exist',
          );
        }

        if (isRequired) {
          yupValidation = yupValidation.required();
        }

        return yupValidation;
      }

      /** BOOLEANS */

      const isBoolean = validations.some((validation: ValidationInfo) => validation.type == ValidationTypes.IsBoolean);

      if (isBoolean) {
        let yupValidation = yup.boolean();

        if (isRequired) {
          yupValidation = yupValidation.required();
        }

        return yupValidation;
      }

      let yupValidation = yup.mixed();

      if (isRequired) {
        yupValidation = yupValidation.required();
      }

      return yupValidation;
    };

    if (path && path.length > 0) {
      const next = path.shift()!;

      return buildFieldValidation(mapping[next]);
    }

    const schemaObj = Object.entries(mapping).reduce(
      (acc: Record<string, yup.AnySchema>, [header, validations]: [string, ValidationInfo[]]) => {
        return { ...acc, [header]: buildFieldValidation(validations) };
      },
      {},
    );

    return yup.object().shape(schemaObj);
  };

  return make() as ForPath extends null ? yup.AnyObjectSchema : yup.AnySchema;
};
